/**
 * Render de los papeles individuales — Fase 8b + 8e.
 *
 * Campaña:
 *  - sprite (`campania.png`), imán, título, cover opcional.
 *  - Debajo del cover: nº de misiones voladas si > 0.
 * Misión:
 *  - sprite (`mision.png`), imán, título.
 *  - Si pertenece a una campaña, su nombre va debajo del título.
 *
 * El `magnet` determina la inclinación visual del papel mediante una
 * clase CSS: `paper--magnet-alas` (−6°), `paper--magnet-beer` (0°),
 * `paper--magnet-estrella` (+6°). Cada imán también tiene su propia
 * posición/tamaño definidos en `operaciones.css`.
 *
 * Read-only: sin handlers de click ni cursor pointer.
 */

import type {
  CampaignSummary,
  MagnetType,
  MissionSummary,
} from '@51esc/shared';

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function magnetClass(m: MagnetType): string {
  return `paper--magnet-${m}`;
}

export function renderCampaignPaper(c: CampaignSummary): string {
  const cover = c.hasCover
    ? `<img class="paper-cover" src="/api/campaigns/${encodeURIComponent(c.id)}/cover" alt="" />`
    : '';
  // Bajo el cover se pintan dos líneas: voladas (X) y planificadas (Y).
  // Ambas SIEMPRE visibles en la zona activa (incluso con 0) para que
  // el papel mantenga un layout estable. En la zona closed se ocultan
  // por CSS (no caben en el papel reducido).
  const flownLabel =
    c.flownMissionsCount === 1
      ? '1 misión volada'
      : `${String(c.flownMissionsCount)} misiones voladas`;
  const plannedLabel =
    c.plannedMissionsCount === 1
      ? '1 misión planificada'
      : `${String(c.plannedMissionsCount)} misiones planificadas`;
  const flown = `<span class="paper-flown-count">${flownLabel}</span>`;
  const planned = `<span class="paper-planned-count">${plannedLabel}</span>`;
  return `
    <article
      class="paper paper--campaign ${magnetClass(c.magnet)}"
      data-paper-kind="campaign"
      data-paper-id="${escapeHtml(c.id)}"
      tabindex="0"
      role="button"
      aria-label="Campaña ${escapeHtml(c.name)}"
    >
      <img class="paper-sprite" src="/operaciones/campania.png" alt="" aria-hidden="true" />
      <img class="paper-magnet" src="/operaciones/${c.magnet}.png" alt="" aria-hidden="true" />
      <h3 class="paper-title paper-title--campaign">${escapeHtml(c.name)}</h3>
      ${cover}
      ${flown}
      ${planned}
    </article>
  `;
}

export function renderMissionPaper(m: MissionSummary): string {
  const campaignLine = m.campaignName
    ? `<span class="paper-campaign-name">${escapeHtml(m.campaignName)}</span>`
    : '';
  // `briefingId` se embebe como data attribute para que el click handler
  // de la pizarra (8e) sepa si navegar al editor o pintar el modal
  // "a la espera de briefing".
  const briefingAttr = m.briefingId
    ? ` data-paper-briefing-id="${escapeHtml(m.briefingId)}"`
    : '';
  return `
    <article
      class="paper paper--mission ${magnetClass(m.magnet)}"
      data-paper-kind="mission"
      data-paper-id="${escapeHtml(m.id)}"${briefingAttr}
      tabindex="0"
      role="button"
      aria-label="Misión ${escapeHtml(m.name)}"
    >
      <img class="paper-sprite" src="/operaciones/mision.png" alt="" aria-hidden="true" />
      <img class="paper-magnet" src="/operaciones/${m.magnet}.png" alt="" aria-hidden="true" />
      <h3 class="paper-title paper-title--mission">${escapeHtml(m.name)}</h3>
      ${campaignLine}
    </article>
  `;
}
